import classnames from 'classnames';
import { useEffect, useMemo, useRef } from 'react';
import type { RefObject } from 'react';
import { Icon } from '@sorrem-client/ui/components';
import styles from './ViewControls.module.scss';

export interface ViewControlsProps {
    readonly zoom: number;
    readonly zoomLevels?: readonly number[];
    readonly onZoomChange: (zoom: number) => void;
    readonly title?: string;
    readonly enableMouseWheel?: boolean;
    readonly containerRef?: RefObject<HTMLElement>;
    /** Extra class on the toolbar, e.g. to place it over a canvas. */
    readonly className?: string;
}

const DEFAULT_ZOOM_LEVELS: readonly number[] = [0.25, 0.5, 1, 2, 4];

export function ViewControls({
    zoom,
    zoomLevels = DEFAULT_ZOOM_LEVELS,
    onZoomChange,
    title = 'View Controls',
    enableMouseWheel = false,
    containerRef,
    className,
}: ViewControlsProps): JSX.Element {
    const sortedLevels = useMemo(() => [...zoomLevels].sort((a, b) => a - b), [zoomLevels]);
    // Step to the nearest level below/above the current zoom, so the buttons
    // also work when a caller zooms continuously between levels (e.g. the
    // skill tree's wheel zoom), not only when `zoom` is exactly a level.
    const lowerLevel = [...sortedLevels].reverse().find((level) => level < zoom - 0.01);
    const higherLevel = sortedLevels.find((level) => level > zoom + 0.01);
    const canZoomOut = lowerLevel !== undefined;
    const canZoomIn = higherLevel !== undefined;

    // The wheel steps from the latest zoom, without re-attaching the listener each time it changes.
    const wheelStepRef = useRef({ lowerLevel, higherLevel, onZoomChange });
    wheelStepRef.current = { lowerLevel, higherLevel, onZoomChange };

    useEffect(() => {
        if (!enableMouseWheel || !containerRef?.current) return;

        const handleWheel = (event: WheelEvent): void => {
            event.preventDefault();

            const step = wheelStepRef.current;
            const nextZoom = event.deltaY > 0 ? step.lowerLevel : step.higherLevel;
            if (nextZoom !== undefined) {
                step.onZoomChange(nextZoom);
            }
        };

        const container = containerRef.current;
        container.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            container.removeEventListener('wheel', handleWheel);
        };
    }, [enableMouseWheel, containerRef]);

    const handleZoomOut = (): void => {
        if (lowerLevel !== undefined) {
            onZoomChange(lowerLevel);
        }
    };

    const handleZoomIn = (): void => {
        if (higherLevel !== undefined) {
            onZoomChange(higherLevel);
        }
    };

    return (
        <div className={classnames(styles.controls, className)} title={title}>
            {/* Native buttons: these controls are styled as a compact overlay
                toolbar, not as the project's Button component. */}
            <button
                type="button"
                className={styles.button}
                onClick={handleZoomOut}
                disabled={!canZoomOut}
                aria-label="Zoom out"
                title="Zoom out"
            >
                <Icon icon="zoom-out" size="small" />
            </button>

            <div className={styles.zoomDisplay}>
                {Math.round(zoom * 100)}%
            </div>

            <button
                type="button"
                className={styles.button}
                onClick={handleZoomIn}
                disabled={!canZoomIn}
                aria-label="Zoom in"
                title="Zoom in"
            >
                <Icon icon="zoom-in" size="small" />
            </button>

        </div>
    );
}
